/** @jsxImportSource @emotion/react */
import {
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  useDisclosure,
} from "@chakra-ui/react";
import styled from "@emotion/styled";
import { Editor } from "@tiptap/react";
import { useEffect, useRef } from "react";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { SetterOrUpdater } from "recoil";
import { IEmailAtom } from "../../atoms/EmailAtom";
import { SUBJECT_KEY } from "../../hook/useEmailEditorHistory";
import useMediaQuery from "../../hook/useMediaQuery";

const Row = styled.div`
  width: 100%;

  padding: 10px;

  position: relative;

  display: flex;
  align-items: center;
  justify-content: space-between;

  font-size: 15px;
  font-weight: 400;
  color: #2f2f2f;

  cursor: pointer;

  &:first-of-type {
    border-radius: 6px 6px 0 0;
  }

  &:last-of-type {
    border-radius: 0 0 6px 6px;
  }

  &:hover {
    background-color: #fb972e;
    color: #ffffff;
  }
`;

const HiddenButton = styled.div`
  position: absolute;
  background: transparent;
  width: 100%;
  height: 100%;
  z-index: 1;
`;

const PopoverContainer = styled.div`
  position: relative;
  cursor: pointer;
  :hover {
    background-color: #f7fafc;
  }
`;

type Props = {
  children: JSX.Element;
  isCampaign?: boolean;
  selectedVariable?: (vle: string) => void;
  setEmail?: SetterOrUpdater<IEmailAtom>;
  removeVirtualFields?: boolean;
} & (
  | { editor?: Editor; isSetting: boolean }
  | { editor: Editor; isSetting?: boolean }
);

interface PopoverSkeletonProps {
  label: string;
  buttons?: { label: string; onClick: () => void }[];
}

function PopoverSkeleton({ label, buttons }: PopoverSkeletonProps) {
  const isLargerThan600 = useMediaQuery("(min-width: 600px)");

  return (
    <Popover
      trigger="hover"
      placement={isLargerThan600 ? "right-start" : "left-start"}
    >
      <PopoverTrigger>
        <Row>
          {!isLargerThan600 && <MdKeyboardArrowLeft size={20} />}
          {label}
          {isLargerThan600 && <MdKeyboardArrowRight size={20} />}
        </Row>
      </PopoverTrigger>
      <PopoverContent w={isLargerThan600 ? "230px" : "170px"}>
        <PopoverArrow />
        <PopoverBody p="0" maxH="160px" overflowY="auto">
          {buttons.map((button) => (
            <Row key={button.label} onClick={button.onClick}>
              {button.label}
            </Row>
          ))}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}

export default function MergeFields(props: Props) {
  const {
    children,
    isSetting,
    isCampaign: isCampaing,
    selectedVariable,
    removeVirtualFields,
    editor,
    setEmail,
  } = props;
  const activeElement = useRef(null);
  const cursorPosition = useRef(null);

  const isLargerThan600 = useMediaQuery("(min-width: 600px)");
  const { isOpen, onToggle, onClose } = useDisclosure();

  function insertMerge(key: string) {
    if (activeElement.current.id === SUBJECT_KEY) {
      const newValue =
        activeElement.current.value.slice(0, cursorPosition.current) +
        `{{Contact.${key}}}` +
        activeElement.current.value.slice(cursorPosition.current);

      setEmail && setEmail((prev) => ({ ...prev, subject: newValue }));
      onClose();
    } else if (isCampaing) {
      editor.chain().focus().insertContent(`{{Contact.${key}}}`).run();
    } else if (isSetting) {
      selectedVariable(`{{Contact.${key}}}`);
    } else {
      editor.chain().focus().insertContent(`{{Contact.${key}}}`).run();
    }
  }

  const NAMES_ARRAY = [
    { onClick: () => insertMerge("Salutation"), label: "Salutation" },
    { onClick: () => insertMerge("FirstName"), label: "First Name" },
    { onClick: () => insertMerge("LastName"), label: "Last Name" },
    ...(!removeVirtualFields
      ? [
          {
            onClick: () => insertMerge("FullName"),
            label: "Full Name",
          },
        ]
      : []),
  ];

  const BILLING_ARRAY = [
    ...(!removeVirtualFields
      ? [
          {
            onClick: () => insertMerge("BillingAddress1Line"),
            label: "Full Address Adrress (One line)",
          },
          {
            onClick: () => insertMerge("BillingAddressBlock"),
            label: "Full Address Address (Block)",
          },
        ]
      : []),
    { onClick: () => insertMerge("BillingStreet1"), label: "Street1" },
    { onClick: () => insertMerge("BillingStreet2"), label: "Street2" },
    { onClick: () => insertMerge("BillingCity"), label: "City" },
    { onClick: () => insertMerge("BillingCountry"), label: "Country" },
    { onClick: () => insertMerge("BillingState"), label: "State" },
    {
      onClick: () => insertMerge("BillingPostalCode"),
      label: "PostalCode",
    },
    {
      onClick: () => insertMerge("BillingPhoneCountryCode"),
      label: "PhoneCountryCode",
    },
    { onClick: () => insertMerge("BillingPhone"), label: "Phone" },
  ];

  const SHIPPING_ARRAY = [
    ...(!removeVirtualFields
      ? [
          {
            onClick: () => insertMerge("ShippingAddress1Line"),
            label: "Full Address Adrress (One line)",
          },
          {
            onClick: () => insertMerge("ShippingAddressBlock"),
            label: "Full Address Address (Block)",
          },
        ]
      : []),
    { onClick: () => insertMerge("ShippingStreet1"), label: "Street1" },
    { onClick: () => insertMerge("ShippingStreet2"), label: "Street2" },
    { onClick: () => insertMerge("ShippingCity"), label: "City" },
    { onClick: () => insertMerge("ShippingCountry"), label: "Country" },
    { onClick: () => insertMerge("ShippingState"), label: "State" },
    {
      onClick: () => insertMerge("ShippingPostalCode"),
      label: "PostalCode",
    },
    {
      onClick: () => insertMerge("ShippingPhoneCountryCode"),
      label: "PhoneCountryCode",
    },
    { onClick: () => insertMerge("ShippingPhone"), label: "Phone" },
  ];

  return (
    <PopoverContainer>
      <HiddenButton
        onMouseDown={(e) => {
          /*
            When click in the popover, 
            save the active element and the cursor position to later check 
            if it is the subject that is selected and inject the merge fields at the cursor position
          */
          activeElement.current = document.activeElement;
          cursorPosition.current = activeElement.current.selectionStart;
          onToggle();
        }}
      />
      <Popover isOpen={isOpen} onClose={onClose}>
        <PopoverTrigger>{children}</PopoverTrigger>
        <PopoverContent w="160px">
          <PopoverArrow />
          <PopoverBody p="0">
            <PopoverSkeleton buttons={NAMES_ARRAY} label="Name" />
            <Row
              style={{
                justifyContent: isLargerThan600 ? "flex-start" : "flex-end",
              }}
              onClick={() => insertMerge("Email")}
            >
              Email
            </Row>
            <Row
              style={{
                justifyContent: isLargerThan600 ? "flex-start" : "flex-end",
              }}
              onClick={() => insertMerge("Phone")}
            >
              Phone
            </Row>
            <PopoverSkeleton buttons={BILLING_ARRAY} label="Billing Address" />
            <PopoverSkeleton
              buttons={SHIPPING_ARRAY}
              label="Shipping Address"
            />
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </PopoverContainer>
  );
}
